import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faChevronLeft, faPaw, faUserPlus } from '@fortawesome/pro-light-svg-icons';
import { faIdCardClip } from '@fortawesome/pro-regular-svg-icons';
import { InboxContact } from '@spike/omnichannel-model';
import Button from 'components/UI/V3/Button/Button';
import useIsMobile from 'hooks/useIsMobile';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { InboxPath } from 'routes/Paths';
import { hashForUrl } from 'utils/HashUtils';
import moment from 'moment-timezone';
import { useAuth } from 'hooks';
import { formatDate, formatShortDateTime } from 'utils/DateUtils';
import InboxAvatar from '../InboxAvatar/InboxAvatar';
import styles from './ThreadHeader.module.scss';

interface ThreadHeaderProps {
    contact: InboxContact;
    conversationId?: number;
    onBack?: () => void;
    onAddNote?: () => void;
    onCreateClient?: () => void;
    /** Omitted when booking is not available here — e.g. the inbox is on a location other than the login one. */
    onBook?: () => void;
}

export const ThreadHeader: React.FC<ThreadHeaderProps> = ({
    contact,
    conversationId,
    onBack,
    onCreateClient,
    onBook
}) => {
    const history = useHistory();
    const isMobile = useIsMobile();
    const auth = useAuth();
    const isProspect = contact.type === 'prospect';

    // the API sends ISO; the UI formats like the rest of the platform, in the marketplace timezone
    const timeZone = auth.timeZone!;
    const clientSince = contact.clientSince ? formatDate(moment(contact.clientSince), timeZone) : undefined;
    const nextBooking = contact.nextBooking ? formatShortDateTime(moment(contact.nextBooking), timeZone) : undefined;

    return (
        <header className={styles.root}>
            {onBack && (
                <button
                    type="button"
                    className={styles.back}
                    onClick={onBack}
                    aria-label="Back to conversations"
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
            )}

            <InboxAvatar
                name={contact.name}
                isProspect={isProspect}
                size={isMobile ? 'medium' : 'large'}
            />

            <div className={styles.info}>
                <div className={styles.nameRow}>
                    <span className={styles.name}>{contact.name}</span>
                    {isProspect && <span className={styles.prospectTag}>New lead</span>}
                    {!isMobile && clientSince && <span className={styles.since}>Client since {clientSince}</span>}
                </div>

                <div className={styles.contextRow}>
                    {contact.pets && contact.pets.length > 0 && (
                        <span className={styles.contextItem}>
                            <FontAwesomeIcon icon={faPaw} />
                            {contact.pets.join(', ')}
                        </span>
                    )}
                    {nextBooking && (
                        <span className={styles.contextItem}>
                            <FontAwesomeIcon icon={faCalendarDays} />
                            Next: {nextBooking}
                        </span>
                    )}
                    {isProspect && (
                        <span className={styles.contextItem}>No client profile yet — replies stay in Inbox</span>
                    )}
                </div>
            </div>

            {/* Shown at every width: under 960px the labels collapse to icons, the way /sms does it. */}
            <div className={styles.actions}>
                {isProspect ? (
                    <Button
                        label="Create client"
                        aria-label="Create client"
                        title="Create client"
                        variant="outlined"
                        size="small"
                        leftIcon={faUserPlus}
                        onlyIcon={isMobile}
                        onClick={onCreateClient}
                    />
                ) : (
                    <>
                        <Button
                            label="View client"
                            aria-label="View client"
                            title="View client"
                            variant="outlined"
                            size="small"
                            leftIcon={isMobile ? faIdCardClip : undefined}
                            onlyIcon={isMobile}
                            onClick={() =>
                                history.push(`/clients/${hashForUrl({ clientId: contact.id })}`, {
                                    returnTo: InboxPath,
                                    conversationId
                                })
                            }
                        />
                        {onBook && (
                            <Button
                                label="Book"
                                variant="green"
                                size="small"
                                onClick={onBook}
                            />
                        )}
                    </>
                )}
                {/* Add note hidden for now */}
            </div>
        </header>
    );
};

export default ThreadHeader;
